// ── FIRM CREDIT CARDS SECTION ─────────────────────────────────────────────────
// Owner-only catalog of every credit card the firm uses, plus who can
// charge each one and for what kinds of expenses. Receipts get tagged
// with which card paid them so monthly reconciliation against QB is
// straightforward.
//
// PCI: stores last 4 digits only -- never the full PAN. The label
// + type + last_4 + holder is enough to identify the card in QB.

import React, { useMemo, useState } from 'react';
import { useAppContext } from './AppContext';

const KIND_OPTIONS = [
  { value: 'vendor_deposit',       label: 'Vendor Deposits' },
  { value: 'vendor_final',         label: 'Vendor Finals' },
  { value: 'vendor_fee',           label: 'Vendor Fees (freight, crating, etc.)' },
  { value: 'project_reimbursable', label: 'Reimbursables (travel, storage)' },
  // DB enum is 'personal'; UI label is "Office" (firm overhead — parking,
  // office supplies, business meals, etc., not billable to a project).
  { value: 'personal',             label: 'Office (firm overhead)' },
];

// Card types vs. non-card payment methods. Last 4 digits is REQUIRED for
// card brands; optional for everything else (Zelle / ACH / wire / BillPay
// / check / cash). Non-card non-cash methods are owner-only — staff only
// pay with cards or cash, never bank transfers.
// Cimon doesn't carry Mastercard or Discover. Schema still allows them
// (DB CHECK is intentionally inclusive in case that changes later) --
// just hidden from the UI dropdown for now.
const CARD_TYPES = [
  { value: 'amex',       label: 'American Express',  category: 'card' },
  { value: 'visa',       label: 'Visa',              category: 'card' },
  { value: 'cash',       label: 'Cash',              category: 'cash' },
  { value: 'zelle',      label: 'Zelle',             category: 'owner_only' },
  { value: 'ach',        label: 'ACH transfer',      category: 'owner_only' },
  { value: 'wire',       label: 'Wire transfer',     category: 'owner_only' },
  { value: 'billpay',    label: 'BillPay',           category: 'owner_only' },
  { value: 'check',      label: 'Check',             category: 'owner_only' },
  { value: 'other',      label: 'Other',             category: 'card' },
];

// Validation list still names all card brands so legacy rows render
// correctly and a future re-enable doesn't silently change behavior.
const CARD_BRANDS = ['amex', 'visa', 'mastercard', 'discover'];
const OWNER_EMAIL = 'c@cimonstarr.com';

function categoryFor(type) {
  var t = CARD_TYPES.find(function(x) { return x.value === type; });
  return (t && t.category) || 'card';
}

function isCardBrand(type) { return CARD_BRANDS.indexOf(type) >= 0; }

const TYPE_DOT = {
  amex:       '#006FCF',
  visa:       '#1A1F71',
  mastercard: '#EB001B',
  discover:   '#FF6000',
  cash:       '#1A5C38',
  zelle:      '#6D1ED4',
  ach:        '#0066c0',
  wire:       '#0066c0',
  billpay:    '#888888',
  check:      '#555555',
  other:      '#888888',
};

function blankCard(userEmail) {
  return {
    label: '',
    cardType: 'visa',
    last4: '',
    holderEmail: userEmail || '',
    allowedKinds: [],   // empty = all kinds
    visibility: 'private',
    visibleToEmails: [],
    billingZip: '',
    statementEmail: '',
    expiration: '',
    notes: '',
    isActive: true,
  };
}

const VISIBILITY_OPTIONS = [
  { value: 'private', label: 'Just the holder', help: 'Only the cardholder sees this on their receipt dropdown.' },
  { value: 'team',    label: 'Specific people', help: 'Pick which staff can use this card.' },
  { value: 'firm',    label: 'Everyone',        help: 'Every authenticated user can use this card. Use for shared firm cards.' },
];

export default function FirmCreditCardsSection() {
  const ctx = useAppContext();
  const cards = ctx.firmCreditCards || [];
  const saveCreditCard = ctx.saveCreditCard;
  const deleteCreditCard = ctx.deleteCreditCard;
  const userEmail = ctx.userEmail || '';

  const [editing, setEditing] = useState(null);  // card object being edited (or new card if no id)
  const [error, setError] = useState('');

  const sorted = useMemo(function() {
    return [...cards].sort(function(a, b) {
      // Active first, then by holder, then by label
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      var h = (a.holderEmail || '').localeCompare(b.holderEmail || '');
      if (h !== 0) return h;
      return (a.label || '').localeCompare(b.label || '');
    });
  }, [cards]);

  function startNew() {
    setEditing(blankCard(userEmail));
    setError('');
  }

  function startEdit(card) {
    setEditing(Object.assign({}, card));
    setError('');
  }

  async function save() {
    if (!editing) return;
    if (!editing.label) { setError('Label is required.'); return; }
    if (!editing.holderEmail) { setError('Holder email is required.'); return; }

    // Card brands must have a 4-digit last_4. Non-card methods can be
    // empty or 4 digits (bank account suffix).
    if (isCardBrand(editing.cardType)) {
      if (!/^[0-9]{4}$/.test(String(editing.last4 || ''))) {
        setError('Card brands require exactly 4 digits in Last 4.');
        return;
      }
    } else if (editing.last4 && !/^[0-9]{4}$/.test(String(editing.last4))) {
      setError('Last 4 must be empty or exactly 4 digits.');
      return;
    }

    // Owner-only payment methods (Zelle / ACH / wire / BillPay / check)
    // can't be assigned to a non-owner holder. Staff pay only with
    // cards or cash.
    var cat = categoryFor(editing.cardType);
    var holderIsOwner = (editing.holderEmail || '').toLowerCase() === OWNER_EMAIL;
    if (cat === 'owner_only' && !holderIsOwner) {
      setError('Zelle / ACH / wire / BillPay / check are owner-only methods. Staff card holders can only use cards or cash.');
      return;
    }

    setError('');
    const ok = await saveCreditCard(editing);
    if (ok) setEditing(null);
    else setError('Save failed. See console.');
  }

  function cancel() {
    setEditing(null);
    setError('');
  }

  function toggleAllowedKind(kind) {
    setEditing(function(prev) {
      const current = prev.allowedKinds || [];
      const next = current.indexOf(kind) >= 0
        ? current.filter(function(k) { return k !== kind; })
        : current.concat([kind]);
      return Object.assign({}, prev, { allowedKinds: next });
    });
  }

  return (
    <div style={{ padding: '20px 18px', maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 16 }}>Payment Methods</div>
        <div style={{ fontSize: 11, color: '#888' }}>
          Cards (Amex, Visa) plus owner-only methods (Zelle, ACH, wire, BillPay, check, cash). Receipts auto-link to a card by last 4 digits during extraction.
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={startNew}
          style={{ padding: '8px 16px', background: '#111', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
          + Add method
        </button>
      </div>

      <div style={{ marginBottom: 18, padding: '10px 14px', background: '#FFF8E1', borderLeft: '3px solid #F5C800', fontSize: 11, color: '#5C4400', borderRadius: 4 }}>
        <strong>Privacy:</strong> only you (owner) can see this full list and write to it. Each card holder can see their own card on their receipt-upload dropdown but nothing else here. Last 4 digits only — never store the full card number.
      </div>

      {error && (
        <div style={{ marginBottom: 12, padding: '8px 12px', background: '#FDECEA', border: '0.5px solid #F2A39C', color: '#C8322A', fontSize: 12, borderRadius: 6 }}>{error}</div>
      )}

      {/* Edit form */}
      {editing && (
        <div style={{ marginBottom: 18, padding: 18, background: '#fff', border: '1px solid #111', borderRadius: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>{editing.id ? 'Edit method' : 'Add payment method'}</div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 12 }}>
            <Field label="Label"
              value={editing.label}
              onChange={function(v) { setEditing(Object.assign({}, editing, { label: v })); }}
              placeholder="Cimon Amex Platinum" />
            <Field label="Holder email"
              value={editing.holderEmail}
              onChange={function(v) { setEditing(Object.assign({}, editing, { holderEmail: v.toLowerCase() })); }}
              placeholder="quinn@cimonstarr.com" />

            <Select label="Method type" value={editing.cardType}
              options={CARD_TYPES}
              onChange={function(v) { setEditing(Object.assign({}, editing, { cardType: v })); }} />
            <Field label={isCardBrand(editing.cardType) ? 'Last 4 digits' : 'Last 4 (optional)'}
              value={editing.last4}
              onChange={function(v) { setEditing(Object.assign({}, editing, { last4: v.replace(/[^0-9]/g, '').slice(0, 4) })); }}
              placeholder={isCardBrand(editing.cardType) ? '1234' : 'optional bank account suffix'}
              maxLength={4} />

            <Field label="Billing zip"
              value={editing.billingZip}
              onChange={function(v) { setEditing(Object.assign({}, editing, { billingZip: v })); }}
              placeholder="94118" />
            <Field label="Statement email"
              value={editing.statementEmail}
              onChange={function(v) { setEditing(Object.assign({}, editing, { statementEmail: v })); }}
              placeholder="finance@cimonstarr.com" />

            <Field label="Expiration (MM/YY)"
              value={editing.expiration}
              onChange={function(v) { setEditing(Object.assign({}, editing, { expiration: v })); }}
              placeholder="08/27" />
            <div>
              <Lbl>Status</Lbl>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#444', cursor: 'pointer' }}>
                <input type="checkbox" checked={!!editing.isActive}
                  onChange={function(e) { setEditing(Object.assign({}, editing, { isActive: e.target.checked })); }} />
                Active
              </label>
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <Lbl>Allowed for these expense kinds</Lbl>
            <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>
              Leave all unchecked to allow this card on any kind. Otherwise the holder can only pick this card on the receipt kinds you check below.
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {KIND_OPTIONS.map(function(k) {
                const on = (editing.allowedKinds || []).indexOf(k.value) >= 0;
                return (
                  <button key={k.value} onClick={function() { toggleAllowedKind(k.value); }}
                    style={{
                      padding: '6px 12px', fontSize: 11,
                      background: on ? '#1A5C38' : '#fff',
                      color: on ? '#fff' : '#444',
                      border: '0.5px solid ' + (on ? '#1A5C38' : '#E0E0DC'),
                      borderRadius: 16, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500,
                    }}>
                    {on ? '✓ ' : ''}{k.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <Lbl>Who can use this card</Lbl>
            <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>
              Cardholder of record (above) is who reconciles statements. This setting controls who sees it on the receipt "Paid with" dropdown.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {VISIBILITY_OPTIONS.map(function(v) {
                var on = (editing.visibility || 'private') === v.value;
                return (
                  <label key={v.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 12px', border: '0.5px solid ' + (on ? '#111' : '#E0E0DC'), borderRadius: 6, cursor: 'pointer', background: on ? '#FAFAF8' : '#fff' }}>
                    <input type="radio" name="cardVisibility" checked={on}
                      onChange={function() { setEditing(Object.assign({}, editing, { visibility: v.value })); }}
                      style={{ marginTop: 2 }} />
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#111' }}>{v.label}</div>
                      <div style={{ fontSize: 10, color: '#888' }}>{v.help}</div>
                    </div>
                  </label>
                );
              })}
            </div>

            {(editing.visibility === 'team') && (
              <div style={{ marginTop: 10, padding: '10px 12px', background: '#FAFAF8', border: '0.5px solid #E0E0DC', borderRadius: 6 }}>
                <Lbl>Visible to (one email per line)</Lbl>
                <textarea
                  value={(editing.visibleToEmails || []).join('\n')}
                  rows={3}
                  onChange={function(e) {
                    var emails = String(e.target.value || '').split(/[\n,]+/).map(function(x){ return x.trim().toLowerCase(); }).filter(Boolean);
                    setEditing(Object.assign({}, editing, { visibleToEmails: emails }));
                  }}
                  placeholder={"quinn@cimonstarr.com\ntim@cimonstarr.com"}
                  style={{ width: '100%', padding: '8px 10px', fontSize: 12, fontFamily: 'inherit', border: '0.5px solid #E0E0DC', borderRadius: 5, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }} />
                <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                  The cardholder always has access regardless of this list.
                </div>
              </div>
            )}
          </div>

          <div style={{ marginBottom: 14 }}>
            <Lbl>Notes</Lbl>
            <textarea value={editing.notes} rows={2}
              onChange={function(e) { setEditing(Object.assign({}, editing, { notes: e.target.value })); }}
              style={{ width: '100%', padding: '8px 10px', fontSize: 12, fontFamily: 'inherit', border: '0.5px solid #E0E0DC', borderRadius: 5, outline: 'none', boxSizing: 'border-box', resize: 'vertical' }}
              placeholder="Virtual number for procurement, expires 08/27" />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={cancel}
              style={{ padding: '8px 16px', background: '#fff', border: '0.5px solid #E0E0DC', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>
              Cancel
            </button>
            <button onClick={save}
              style={{ padding: '8px 18px', background: '#111', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
              {editing.id ? 'Save changes' : 'Add method'}
            </button>
          </div>
        </div>
      )}

      {/* Card list */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden' }}>
        {sorted.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: '#888', fontSize: 12, fontStyle: 'italic' }}>
            No cards yet. Click + Add card to register your first.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#FAFAF8', borderBottom: '0.5px solid #E0E0DC' }}>
                <th style={th}>Card</th>
                <th style={th}>Holder</th>
                <th style={th}>Visible to</th>
                <th style={th}>Allowed for</th>
                <th style={th}>Exp</th>
                <th style={th}>Status</th>
                <th style={Object.assign({}, th, { textAlign: 'right' })}></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(function(c) {
                const allowedLabel = (!c.allowedKinds || c.allowedKinds.length === 0)
                  ? 'All kinds'
                  : c.allowedKinds.map(function(k) {
                      const opt = KIND_OPTIONS.find(function(o) { return o.value === k; });
                      return opt ? opt.label : k;
                    }).join(', ');
                return (
                  <tr key={c.id} style={{ borderBottom: '0.5px solid #f0f0f0', opacity: c.isActive ? 1 : 0.5 }}>
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: TYPE_DOT[c.cardType] || '#888' }} />
                        <div>
                          <div style={{ fontWeight: 600, color: '#111' }}>{c.label}</div>
                          <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {(CARD_TYPES.find(function(t) { return t.value === c.cardType; }) || {}).label}
                            {c.last4 ? ' · ••• ' + c.last4 : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={td}>{c.holderEmail}</td>
                    <td style={Object.assign({}, td, { color: '#666', fontSize: 11 })}>
                      {c.visibility === 'firm'
                        ? 'Everyone'
                        : c.visibility === 'team'
                          ? ((c.visibleToEmails && c.visibleToEmails.length)
                              ? ('Holder + ' + c.visibleToEmails.length + ' staff')
                              : 'Holder only (no team set)')
                          : 'Just the holder'}
                    </td>
                    <td style={Object.assign({}, td, { color: '#666', fontSize: 11 })}>{allowedLabel}</td>
                    <td style={td}>{c.expiration || '—'}</td>
                    <td style={td}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                        background: '#fff',
                        border: '0.5px solid ' + (c.isActive ? '#1A5C38' : '#888'),
                        color: c.isActive ? '#1A5C38' : '#888',
                      }}>{c.isActive ? 'ACTIVE' : 'INACTIVE'}</span>
                    </td>
                    <td style={Object.assign({}, td, { textAlign: 'right', whiteSpace: 'nowrap' })}>
                      <button onClick={function() { startEdit(c); }}
                        style={{ padding: '4px 10px', fontSize: 11, background: 'none', border: '0.5px solid #E0E0DC', borderRadius: 4, cursor: 'pointer', color: '#444', fontFamily: 'inherit', marginRight: 4 }}>
                        Edit
                      </button>
                      <button onClick={function() {
                        if (window.confirm('Delete payment method "' + c.label + '"? Receipts that referenced it will lose the link (paid_with_card_id becomes null).')) {
                          deleteCreditCard(c.id);
                        }
                      }}
                        style={{ padding: '4px 10px', fontSize: 11, background: 'none', border: '0.5px solid #E0E0DC', borderRadius: 4, cursor: 'pointer', color: '#888', fontFamily: 'inherit' }}>
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Lbl(p) {
  return <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888', marginBottom: 4 }}>{p.children}</div>;
}

function Field(p) {
  return (
    <div>
      <Lbl>{p.label}</Lbl>
      <input value={p.value || ''} onChange={function(e) { p.onChange(e.target.value); }}
        placeholder={p.placeholder || ''}
        maxLength={p.maxLength}
        style={{ width: '100%', padding: '7px 10px', fontSize: 12, fontFamily: 'inherit', border: '0.5px solid #E0E0DC', borderRadius: 5, outline: 'none', boxSizing: 'border-box' }} />
    </div>
  );
}

function Select(p) {
  return (
    <div>
      <Lbl>{p.label}</Lbl>
      <select value={p.value} onChange={function(e) { p.onChange(e.target.value); }}
        style={{ width: '100%', padding: '7px 10px', fontSize: 12, fontFamily: 'inherit', border: '0.5px solid #E0E0DC', borderRadius: 5, background: '#fff', outline: 'none', boxSizing: 'border-box' }}>
        {(p.options || []).map(function(o) {
          return <option key={o.value} value={o.value}>{o.label}</option>;
        })}
      </select>
    </div>
  );
}

const th = { padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' };
const td = { padding: '12px 12px', color: '#444', verticalAlign: 'middle' };
